import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, type CustomerAgentInfo } from '../lib/api';
import { getAuthProfile, userSignOut, type AuthProfile } from '../lib/auth';
import type { InsightCardPayload } from '../lib/insightCard';
import {
  attachLocalPreview,
  endSession,
  rejoinLiveKitSession,
  resumeRemoteAudio,
  setCameraEnabled,
  setMicEnabled,
  silenceSessionAudio,
  startLiveKitSession,
  type SessionEndReason,
  type SessionHandles,
  type TranscriptLine,
} from '../lib/livekit';
import { LIVEAVATAR_SESSION_LIMIT_LA_LEAVE_MS } from '../lib/liveAvatarSessionLimit';
import { reportLocalMicHaircheck } from '../lib/localMicDiagnostics';
import {
  clearSessionIdentity,
  pathWithKeptQuery,
  readAgentVersionFromUrl,
  readAvatarAudioFromUrl,
  resolveResumeSessionId,
  writeSessionToUrl,
} from '../lib/sessionUrl';

export type Msg = {
  author: 'Agent' | 'You' | 'Live Perception';
  text: string;
  time: string;
  key: string;
  kind?: 'link' | 'text' | 'live_perception_debug';
  url?: string;
  frameUrl?: string;
};

type JoinOpts = {
  firstName: string;
  lastName: string;
  cameraOn: boolean;
  micOn: boolean;
  mediaStream: MediaStream | null;
  resumeSessionId?: string;
  videoDeviceId?: string | null;
  audioDeviceId?: string | null;
};

type SessionContextValue = {
  authReady: boolean;
  profile: AuthProfile | null;
  refreshProfile: () => Promise<void>;
  agentInfo: CustomerAgentInfo | null;
  agentId: string | undefined;
  /** Admin Test call pin (`?av=`). */
  agentVersionPin: 'working' | number | null;
  messages: Msg[];
  input: string;
  setInput: (v: string) => void;
  micMuted: boolean;
  cameraOff: boolean;
  toast: string | null;
  showToast: (message: string) => void;
  /** Browser blocked the agent's audio; show the enable-sound control. */
  audioBlocked: boolean;
  /** Unlock agent audio (must run inside a user gesture). */
  enableAudio: () => Promise<void>;
  connecting: boolean;
  reconnecting: boolean;
  connected: boolean;
  /** Explicit UI phase — do not derive from connected (avoids live chrome before morph). */
  callPhase: 'prep' | 'connecting' | 'morphing' | 'live';
  /** Haircheck→live join morph in progress. */
  joinMorphing: boolean;
  completeJoinMorph: () => void;
  elapsed: string;
  /** LiveAvatar FULL session cap (minutes); null when unknown or unlimited client-side. */
  sessionMaxMinutes: number | null;
  sessionLimitEndsAtMs: number | null;
  sessionId: string;
  shortId: string;
  durationSec: number;
  hasCompletedSession: boolean;
  previewRef: React.RefObject<HTMLDivElement | null>;
  remoteVideoRef: React.RefObject<HTMLVideoElement | null>;
  messagesRef: React.RefObject<HTMLDivElement | null>;
  /** Latest Direct insight card from room data (null when flag off or none yet). */
  insightCard: InsightCardPayload | null;
  /**
   * Session-stamped Direct insight cards flag (from POST /sessions/livekit).
   * Prefer this over agentInfo alone so a stale getAgent cannot hide cards.
   */
  liveInsightCardsEnabled: boolean;
  connectCall: (opts: JoinOpts) => Promise<void>;
  onMic: () => Promise<void>;
  onCamera: () => Promise<void>;
  onHangup: () => void;
  onLeave: () => Promise<void>;
  onNewSession: () => void;
  pushLocalMessage: (text: string) => void;
  copyShortId: () => Promise<void>;
  tryResume: () => void;
};

const SessionContext = createContext<SessionContextValue | null>(null);

const JOIN_MORPH_MS = 650;

function waitForPaint(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });
}

const clockFmt = (ms: number) => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

const stamp = () =>
  new Intl.DateTimeFormat('en', { hour: '2-digit', minute: '2-digit', hour12: false }).format(
    new Date(),
  );

export function SessionProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const agentId = searchParams.get('agent') || undefined;
  const agentVersionPin = readAgentVersionFromUrl(searchParams.toString());

  // Legacy ids — canonical customer link is ?agent=mia
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const lower = (agentId || '').toLowerCase();
    if (lower !== 'lynn' && lower !== 'brian') return;
    const url = new URL(window.location.href);
    url.searchParams.set('agent', 'mia');
    navigate(`${url.pathname}${url.search}${url.hash}`, { replace: true });
  }, [agentId, navigate]);
  const avatarAudioPath = readAvatarAudioFromUrl(searchParams.toString());

  const [profile, setProfile] = useState<AuthProfile | null>(null);
  const [authReady, setAuthReady] = useState(false);
  const [agentInfo, setAgentInfo] = useState<CustomerAgentInfo | null>(null);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState('');
  const [micMuted, setMicMuted] = useState(false);
  const [cameraOff, setCameraOff] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [audioBlocked, setAudioBlocked] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [connected, setConnected] = useState(false);
  const [callPhase, setCallPhase] = useState<'prep' | 'connecting' | 'morphing' | 'live'>('prep');
  const [joinMorphing, setJoinMorphing] = useState(false);
  const [elapsed, setElapsed] = useState('0:00');
  const [sessionMaxMinutes, setSessionMaxMinutes] = useState<number | null>(null);
  const [sessionLimitEndsAtMs, setSessionLimitEndsAtMs] = useState<number | null>(null);
  const [sessionId, setSessionId] = useState('');
  const [shortId, setShortId] = useState('');
  const [durationSec, setDurationSec] = useState(0);
  const [hasCompletedSession, setHasCompletedSession] = useState(false);
  const [insightCard, setInsightCard] = useState<InsightCardPayload | null>(null);
  const [liveInsightCardsEnabled, setLiveInsightCardsEnabled] = useState(false);

  const sessionRef = useRef<SessionHandles | null>(null);
  const endingRef = useRef(false);
  const resumeTried = useRef(false);
  const seenKeys = useRef(new Set<string>());
  const toastTimer = useRef<number | undefined>(undefined);
  const previewRef = useRef<HTMLDivElement | null>(null);
  const remoteVideoRef = useRef<HTMLVideoElement | null>(null);
  const messagesRef = useRef<HTMLDivElement | null>(null);
  /** Latest chat for LiveAvatar FULL hangup flush (messages state may be mid-update). */
  const chatArchiveRef = useRef<Msg[]>([]);
  const morphDoneRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    chatArchiveRef.current = messages;
  }, [messages]);

  const refreshProfile = useCallback(async () => {
    const next = await getAuthProfile();
    setProfile(next);
  }, []);

  useEffect(() => {
    void (async () => {
      try {
        await refreshProfile();
      } finally {
        setAuthReady(true);
      }
    })();
  }, [refreshProfile]);

  useEffect(() => {
    if (!authReady || !profile?.isUser) return;
    let cancelled = false;
    void api
      .getAgent(agentId, { version: agentVersionPin })
      .then((info) => {
        if (!cancelled) {
          setAgentInfo(info);
          // Seed from getAgent; session create may override with the stamped value.
          if (info.liveInsightCardsEnabled === true) {
            setLiveInsightCardsEnabled(true);
          }
        }
      })
      .catch(() => {
        // Keep prior agent on fetch failure so complete page does not flash workspace default.
        if (!cancelled && !agentId) return;
        if (!cancelled) setAgentInfo(null);
      });
    return () => {
      cancelled = true;
    };
  }, [authReady, profile?.isUser, agentId, agentVersionPin]);

  // Preload agent cover as soon as we know the URL so haircheck paints from cache.
  useEffect(() => {
    const url = agentInfo?.avatarUrl?.trim();
    if (!url || typeof document === 'undefined') return;

    const linkId = 'pulse-agent-cover-preload';
    let link = document.getElementById(linkId) as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.id = linkId;
      link.rel = 'preload';
      link.as = 'image';
      document.head.appendChild(link);
    }
    if (link.href !== url) link.href = url;

    const img = new Image();
    img.decoding = 'async';
    img.src = url;
    void img.decode?.().catch(() => undefined);

    return () => {
      // Keep the preload link for the session; only clear on unmount of provider.
    };
  }, [agentInfo?.avatarUrl]);

  useEffect(() => {
    if (!connected || !sessionRef.current) return;
    const started = sessionRef.current.startedAt;
    const tick = () => setElapsed(clockFmt(Date.now() - started));
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [connected]);

  const showToast = useCallback((message: string) => {
    window.clearTimeout(toastTimer.current);
    setToast(message);
    toastTimer.current = window.setTimeout(() => setToast(null), 1800);
  }, []);

  const enableAudio = useCallback(async () => {
    await resumeRemoteAudio(sessionRef.current?.room);
    setAudioBlocked(false);
  }, []);

  const pushInsightCard = useCallback((card: InsightCardPayload) => {
    setInsightCard((prev) => {
      if (prev && card.revision <= prev.revision && card.cardId === prev.cardId) {
        return prev;
      }
      if (prev && card.revision < prev.revision) return prev;
      return card;
    });
  }, []);

  const pushTranscript = useCallback((line: TranscriptLine) => {
    // Live chat is RTVI; Pulse data may still deliver link / LP debug rows.
    // Soft-dedupe LP debug in case archive seed and data race on rejoin.
    const key =
      line.kind === 'live_perception_debug'
        ? `lp_debug:${line.text}:${line.at || ''}`
        : `${line.kind || 'text'}:${line.role}:${line.url || line.text}:${line.at || ''}`;
    if (seenKeys.current.has(key)) return;
    // Also collapse same LP text when seed and data use slightly different `at`.
    if (line.kind === 'live_perception_debug' && line.text) {
      const soft = `lp_debug:${line.text}:`;
      for (const k of seenKeys.current) {
        if (k.startsWith(soft)) return;
      }
    }
    seenKeys.current.add(key);
    setMessages((m) => {
      const author: Msg['author'] =
        line.kind === 'live_perception_debug'
          ? 'Live Perception'
          : line.role === 'user'
            ? 'You'
            : 'Agent';
      const next: Msg = {
        author,
        text: line.text,
        time: stamp(),
        key,
        kind: line.kind === 'link' ? 'link' : line.kind === 'live_perception_debug' ? 'live_perception_debug' : 'text',
        url: line.url,
        frameUrl: line.frameUrl,
      };
      const last = m[m.length - 1];
      // Consecutive fragments from the same speaker stay one bubble (GPT-Live / STT parts).
      if (
        (author === 'You' || author === 'Agent')
        && line.kind !== 'link'
        && line.kind !== 'live_perception_debug'
        && last
        && last.author === author
        && last.kind !== 'link'
        && last.text
        && line.text
      ) {
        seenKeys.current.delete(last.key);
        let text: string;
        if (line.text.startsWith(last.text)) {
          text = line.text;
        } else if (last.text.startsWith(line.text)) {
          text = last.text;
        } else {
          const glue = /[\s]$/.test(last.text) || /^[\s,.;:!?'-]/.test(line.text) ? '' : ' ';
          text = last.text + glue + line.text;
        }
        return [...m.slice(0, -1), { ...next, text, time: last.time }];
      }
      return [...m, next];
    });
  }, []);

  const goToSummary = useCallback(async (endOpts?: { endReason?: SessionEndReason; explicitUserHangup?: boolean }) => {
    if (endingRef.current) return;
    endingRef.current = true;
    clearSessionIdentity();
    const handles = sessionRef.current;
    sessionRef.current = null;
    const started = handles?.startedAt || Date.now();
    setDurationSec(Math.max(1, Math.round((Date.now() - started) / 1000)));
    setHasCompletedSession(true);
    setJoinMorphing(false);
    setAudioBlocked(false);
    setCallPhase('prep');
    // Mute avatar / bot BEFORE flipping to /complete — endSession also awaits
    // client-archive finalize which can take seconds on LiveAvatar FULL.
    silenceSessionAudio(handles);
    // Keep ?agent= / ?av= so complete page shows the session agent, not workspace default.
    try {
      const url = new URL(window.location.href);
      if (!url.searchParams.get('agent')?.trim() && agentInfo?.agentId) {
        url.searchParams.set('agent', agentInfo.agentId);
        window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
      }
    } catch {
      /* ignore */
    }
    navigate(pathWithKeptQuery('/complete'));
    setConnected(false);
    setConnecting(false);
    setReconnecting(false);
    setMicMuted(true);
    setCameraOff(true);
    setSessionMaxMinutes(null);
    setSessionLimitEndsAtMs(null);
    setInsightCard(null);
    setLiveInsightCardsEnabled(false);
    try {
      const transcriptLines = chatArchiveRef.current
        .filter((m) => m.kind !== 'link' && m.kind !== 'live_perception_debug')
        .map((m) => ({
          role: m.author === 'You' ? 'user' : 'assistant',
          text: m.text,
          at: Date.now(),
        }))
        .filter((row) => row.text.trim());
      await endSession(handles, {
        transcriptLines,
        endReason: endOpts?.endReason,
        explicitUserHangup: endOpts?.explicitUserHangup,
      });
    } finally {
      endingRef.current = false;
    }
  }, [navigate, agentInfo?.agentId]);

  const completeJoinMorph = useCallback(() => {
    setJoinMorphing(false);
    morphDoneRef.current?.();
    morphDoneRef.current = null;
  }, []);

  const enterCall = useCallback(
    async (
      handles: SessionHandles,
      opts: { cameraOn: boolean; mediaStream: MediaStream | null; wasRejoin?: boolean },
    ) => {
      sessionRef.current = handles;
      // Prevent tryResume from firing a second join after we write the session id.
      resumeTried.current = true;
      setSessionId(handles.sessionId);
      setShortId(handles.shortId);
      const maxM = handles.sessionPolicy?.sessionMaxMinutes;
      setSessionMaxMinutes(typeof maxM === 'number' && maxM > 0 ? maxM : null);
      const endsAt = handles.sessionLimitEndsAtMs;
      setSessionLimitEndsAtMs(typeof endsAt === 'number' && endsAt > 0 ? endsAt : null);
      writeSessionToUrl(handles.sessionId, agentId, agentVersionPin, avatarAudioPath);
      setConnected(true);
      setCallPhase('live');
      setHasCompletedSession(false);
      showToast(opts.wasRejoin ? 'Reconnected' : 'Connected');
      if (opts.cameraOn && handles.localVideoTrack && !opts.mediaStream) {
        await waitForPaint();
        attachLocalPreview(handles.localVideoTrack, previewRef.current);
      }
    },
    [agentId, agentVersionPin, avatarAudioPath, showToast],
  );

  const connectCall = useCallback(
    async (opts: JoinOpts) => {
      if (!profile?.isUser || connecting || connected) return;
      setConnecting(true);
      setCallPhase('connecting');
      const isResume = Boolean(opts.resumeSessionId);
      if (isResume) setReconnecting(true);
      // Owning a connect attempt — do not let tryResume race another join.
      resumeTried.current = true;

      setMessages([]);
      setInsightCard(null);
      setLiveInsightCardsEnabled(agentInfo?.liveInsightCardsEnabled === true);
      seenKeys.current = new Set();
      setMicMuted(!opts.micOn);
      setCameraOff(!opts.cameraOn);

      try {
        const sessionOpts = {
          getVideoEl: () => remoteVideoRef.current,
          getPreviewEl: () => previewRef.current,
          cameraOn: opts.cameraOn,
          micOn: opts.micOn,
          firstName: opts.firstName,
          lastName: opts.lastName,
          agentId,
          agentVersionPin,
          avatarAudioPath,
          mediaStream: opts.mediaStream,
          showLivePerceptionDebug: profile?.isAdmin === true,
          onTranscript: pushTranscript,
          onInsightCard: pushInsightCard,
          onSessionCreated: (meta: { liveInsightCardsEnabled?: boolean }) => {
            const on = meta.liveInsightCardsEnabled === true;
            setLiveInsightCardsEnabled(on);
            setAgentInfo((prev) =>
              prev ? { ...prev, liveInsightCardsEnabled: on } : prev,
            );
          },
          onDisconnected: () => {
            if (endingRef.current) return;
            const endsAt = sessionRef.current?.sessionLimitEndsAtMs;
            const nearLimit =
              typeof endsAt === 'number' && endsAt - Date.now() <= LIVEAVATAR_SESSION_LIMIT_LA_LEAVE_MS;
            void goToSummary(nearLimit ? { endReason: 'session_limit' } : undefined);
          },
          onArmingTimeout: () => {
            showToast('Session started — recording may be incomplete');
          },
          onAudioBlocked: (blocked: boolean) => setAudioBlocked(blocked),
          onSessionLimitEnd: () => {
            // Safety only — normal LA cap ends via RoomEvent.Disconnected after platform teardown.
            if (!endingRef.current) void goToSummary({ endReason: 'session_limit' });
          },
        };
        // Blocks until room + local A/V + session_armed (or timeout). Greeting audio
      // must not be the unlock gate — that would keep haircheck up while June talks.
        const handles = opts.resumeSessionId
          ? await rejoinLiveKitSession(opts.resumeSessionId, sessionOpts)
          : await startLiveKitSession(sessionOpts);

        void reportLocalMicHaircheck(handles.sessionId, opts.mediaStream);

        if (!isResume) {
          setCallPhase('morphing');
          await new Promise<void>((resolve) => {
            setJoinMorphing(true);
            morphDoneRef.current = resolve;
            window.setTimeout(() => {
              if (morphDoneRef.current === resolve) {
                setJoinMorphing(false);
                morphDoneRef.current = null;
                resolve();
              }
            }, JOIN_MORPH_MS + 400);
          });
        }

        await enterCall(handles, { ...opts, wasRejoin: isResume });
      } catch (e) {
        setJoinMorphing(false);
        morphDoneRef.current = null;
        setConnected(false);
        setCallPhase('prep');
        // Allow a later manual retry / resume after a failed connect.
        if (opts.resumeSessionId) {
          resumeTried.current = false;
          clearSessionIdentity();
          showToast(
            e instanceof Error && /not rejoinable|not found/i.test(e.message)
              ? 'Session ended — start a new one'
              : e instanceof Error
                ? e.message
                : 'Reconnect failed',
          );
        } else {
          showToast(e instanceof Error ? e.message : 'Connect failed');
        }
        opts.mediaStream?.getTracks().forEach((t) => {
          try {
            t.stop();
          } catch {
            /* ignore */
          }
        });
      } finally {
        setConnecting(false);
        setReconnecting(false);
      }
    },
    [
      profile?.isUser,
      profile?.isAdmin,
      connecting,
      connected,
      agentId,
      agentVersionPin,
      avatarAudioPath,
      pushTranscript,
      pushInsightCard,
      goToSummary,
      showToast,
      enterCall,
      agentInfo?.liveInsightCardsEnabled,
    ],
  );

  const tryResume = useCallback(() => {
    if (import.meta.env.VITE_E2E_VISUAL === '1') return;
    if (
      !authReady ||
      !profile?.isUser ||
      resumeTried.current ||
      connecting ||
      connected ||
      callPhase !== 'prep'
    ) {
      return;
    }
    const sid = resolveResumeSessionId();
    if (!sid) return;
    resumeTried.current = true;
    void (async () => {
      let mediaStream: MediaStream | null = null;
      try {
        mediaStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
          video: { facingMode: 'user' },
        });
      } catch {
        /* optional */
      }
      await connectCall({
        firstName: profile.firstName,
        lastName: profile.lastName,
        cameraOn: Boolean(mediaStream?.getVideoTracks().length),
        micOn: Boolean(mediaStream?.getAudioTracks().length),
        mediaStream,
        resumeSessionId: sid,
      });
    })();
  }, [authReady, profile, connecting, connected, callPhase, connectCall]);

  const onMic = useCallback(async () => {
    const next = !micMuted;
    setMicMuted(next);
    if (sessionRef.current) await setMicEnabled(sessionRef.current.room, !next);
    showToast(next ? 'Microphone muted' : 'Microphone on');
  }, [micMuted, showToast]);

  const onCamera = useCallback(async () => {
    const next = !cameraOff;
    setCameraOff(next);
    if (sessionRef.current) {
      await setCameraEnabled(sessionRef.current.room, !next, previewRef.current);
    }
    showToast(next ? 'Camera off' : 'Camera on');
  }, [cameraOff, showToast]);

  const onHangup = useCallback(() => {
    void goToSummary({ endReason: 'hangup', explicitUserHangup: true });
  }, [goToSummary]);

  const onLeave = useCallback(async () => {
    clearSessionIdentity();
    const transcriptLines = chatArchiveRef.current
      .filter((m) => m.kind !== 'link' && m.kind !== 'live_perception_debug')
      .map((m) => ({
        role: m.author === 'You' ? 'user' : 'assistant',
        text: m.text,
        at: Date.now(),
      }))
      .filter((row) => row.text.trim());
    await endSession(sessionRef.current, { transcriptLines });
    sessionRef.current = null;
    setConnected(false);
    setCallPhase('prep');
    setHasCompletedSession(false);
    setMessages([]);
    setInsightCard(null);
    setLiveInsightCardsEnabled(false);
    await userSignOut();
    setProfile(null);
    navigate('/login');
  }, [navigate]);

  const onNewSession = useCallback(() => {
    clearSessionIdentity();
    resumeTried.current = false;
    setMessages([]);
    setInsightCard(null);
    setLiveInsightCardsEnabled(false);
    setHasCompletedSession(false);
    setConnected(false);
    setCallPhase('prep');
    setJoinMorphing(false);
    setSessionId('');
    setShortId('');
    setDurationSec(0);
    setSessionMaxMinutes(null);
    setSessionLimitEndsAtMs(null);
    navigate(pathWithKeptQuery('/haircheck-returning'));
  }, [navigate]);

  const pushLocalMessage = useCallback(
    (text: string) => {
      const value = text.trim();
      if (!value) return;
      pushTranscript({ role: 'user', text: value, at: Date.now() });
    },
    [pushTranscript],
  );

  const copyShortId = useCallback(async () => {
    const id = shortId || sessionId;
    if (!id) return;
    try {
      await navigator.clipboard.writeText(id);
      showToast('Session ID copied');
    } catch {
      showToast(id);
    }
  }, [shortId, sessionId, showToast]);

  const value = useMemo<SessionContextValue>(
    () => ({
      authReady,
      profile,
      refreshProfile,
      agentInfo,
      agentId,
      agentVersionPin,
      messages,
      input,
      setInput,
      micMuted,
      cameraOff,
      toast,
      showToast,
      audioBlocked,
      enableAudio,
      connecting,
      reconnecting,
      connected,
      callPhase,
      joinMorphing,
      completeJoinMorph,
      elapsed,
      sessionMaxMinutes,
      sessionLimitEndsAtMs,
      sessionId,
      shortId,
      durationSec,
      hasCompletedSession,
      previewRef,
      remoteVideoRef,
      messagesRef,
      insightCard,
      liveInsightCardsEnabled,
      connectCall,
      onMic,
      onCamera,
      onHangup,
      onLeave,
      onNewSession,
      pushLocalMessage,
      copyShortId,
      tryResume,
    }),
    [
      authReady,
      profile,
      refreshProfile,
      agentInfo,
      agentId,
      agentVersionPin,
      messages,
      input,
      micMuted,
      cameraOff,
      toast,
      showToast,
      audioBlocked,
      enableAudio,
      connecting,
      reconnecting,
      connected,
      callPhase,
      joinMorphing,
      completeJoinMorph,
      elapsed,
      sessionMaxMinutes,
      sessionLimitEndsAtMs,
      sessionId,
      shortId,
      durationSec,
      hasCompletedSession,
      insightCard,
      liveInsightCardsEnabled,
      connectCall,
      onMic,
      onCamera,
      onHangup,
      onLeave,
      onNewSession,
      pushLocalMessage,
      copyShortId,
      tryResume,
    ],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession requires SessionProvider');
  return ctx;
}
